// ── Countries sidebar ────────────────────────────────────────────────────────
//
// Drop-in replacement for the existing CountriesSidebar function at the bottom
// of IncidentMap.tsx. Adds a search box (filter by country name OR ISO code)
// with the same keyboard-navigation / autofocus pattern as RegionsSidebar in
// CountryView, so the world view behaves identically to the drill-down level.
//
import { useEffect, useMemo, useRef, useState } from 'react';

import Flag from 'react-world-flags';

import { Search } from 'icons';
import { useIntl } from 'localization';

import { TextInput } from '../../atoms';
import { COUNTRY_NAMES, ThresholdConfig, rateColor, rateSeverity } from './MapSharedData';

interface CountriesSidebarProps {
  countryTotals: Record<string, number>;
  countrySums: Record<string, number>;
  /** Per-country orders-sent denominator — drives the min-orders guard. */
  countrySent?: Record<string, number>;
  cfg: ThresholdConfig;
  onSelect: (code: string) => void;
}

/**
 * Sidebar listing all countries that have incidents, sorted by total count
 * descending. Each row shows the flag, country name, severity color, and total.
 * Clicking a row drills into that country (same as clicking the map).
 *
 * Includes a search box to filter by country name OR ISO code — handy now that
 * the list runs to 50+ countries. Mirrors RegionsSidebar exactly: autofocus on
 * mount, ↑↓ to move the highlight (wraps), Enter to select, Escape to clear
 * then blur.
 *
 * Rendered as a flush right-side panel (matching PostcodeSidebar's style in
 * CountryView) so the layout stays consistent at every drill-down level.
 * Visible only on the world view.
 */
function CountriesSidebar({
  countryTotals,
  countrySums,
  countrySent,
  cfg,
  onSelect,
}: CountriesSidebarProps) {
  const { formatMessage } = useIntl();
  const [query, setQuery] = useState('');
  // -1 = nothing highlighted (typing freely). 0+ = a row is highlighted for
  // keyboard selection. Driven by ArrowUp/ArrowDown in the search input.
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Full list of countries with data — sorted by total sum descending.
  const allRows = useMemo(() => {
    return Object.keys(countrySums)
      .filter((code) => (countrySums[code] ?? 0) > 0)
      .map((code) => ({
        code,
        name: COUNTRY_NAMES[code] ?? code,
        sum: countrySums[code] ?? 0,
        max: countryTotals[code] ?? 0,
        sent: countrySent?.[code],
      }))
      .sort((a, b) => b.sum - a.sum);
  }, [countryTotals, countrySums, countrySent]);

  // Filter by case-insensitive substring on either the country name or its
  // ISO code, so "spain" and "es" both find Spain.
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter(
      (r) => r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q),
    );
  }, [allRows, query]);

  // grandTotal / header count always describe the WHOLE dataset, not the
  // filtered view, so the "N countries · M total" header stays stable.
  const grandTotal = useMemo(() => allRows.reduce((a, r) => a + r.sum, 0), [allRows]);

  // Reset highlight whenever the filtered set changes (new query) so we don't
  // point at a stale index.
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [query]);

  // Keep the highlighted row scrolled into view as the user arrows through.
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-row-index="${highlightedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  // Keyboard navigation following the ARIA combobox pattern (identical to
  // RegionsSidebar):
  //   ArrowDown / ArrowUp — move highlight (wraps at the ends)
  //   Enter               — select the highlighted row
  //   Escape              — clear the query (or blur if already empty)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (rows.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => (i + 1) % rows.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => (i <= 0 ? rows.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
      const row = rows[idx];
      if (row) onSelect(row.code);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // don't let any global Escape handler also fire
      if (query) {
        setQuery('');
      } else {
        (e.target as HTMLInputElement).blur();
      }
    }
  };

  return (
    <div className="w-[280px] bg-white border-l border-v2blueGray-200 flex flex-col overflow-hidden shrink-0">
      <div className="px-4 py-3.5 border-b border-v2blueGray-200">
        <div className="text-[11px] text-v2blueGray-500 uppercase tracking-[0.06em] mb-1">
          {formatMessage({ id: 'analytics.incidentMap.countries.header' })}
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-semibold text-v2blueGray-900">{allRows.length}</span>
          <span className="text-[11px] text-v2blueGray-500">
            · {formatMessage({ id: 'analytics.incidentMap.ordersCount' }, { count: grandTotal })}
          </span>
        </div>
      </div>

      {/* Search — filters the country list by name or ISO code */}
      <div className="px-3 py-2 border-b border-v2blueGray-200">
        <TextInput
          name="country-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={formatMessage({ id: 'analytics.incidentMap.countries.searchPlaceholder' })}
          icon={<Search />}
          iconPosition="left"
        />
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto">
        {rows.length === 0 ? (
          <div className="p-6 text-center text-v2blueGray-500 text-[13px]">
            {query
              ? formatMessage({ id: 'analytics.incidentMap.countries.emptySearch' }, { query })
              : formatMessage({ id: 'analytics.incidentMap.countries.empty' })}
          </div>
        ) : (
          rows.map((row, index) => {
            const color = rateColor(row.sum, row.sent, cfg);
            const sev = rateSeverity(row.sum, row.sent, cfg);
            const isHighlighted = index === highlightedIndex;
            return (
              <button
                key={row.code}
                data-row-index={index}
                onClick={() => onSelect(row.code)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`flex items-center gap-2.5 w-full px-4 py-2.5 border-none border-b border-v2blueGray-200 cursor-pointer text-left transition-colors duration-150 ${
                  isHighlighted ? 'bg-v2blueGray-100' : 'bg-transparent hover:bg-v2blueGray-100'
                }`}
              >
                <div
                  className="w-2.5 h-2.5 rounded-full border-[1.5px] border-white shrink-0"
                  style={{ background: color, boxShadow: '0 0 0 1px rgba(31,60,78,0.1)' }}
                />
                <div className="w-6 h-6 flex items-center justify-center shrink-0">
                  <Flag
                    className="rounded-sm object-cover"
                    style={{ width: '18px', height: '14px' }}
                    code={row.code}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[13px] font-medium text-v2blueGray-900 truncate">
                      {row.name}
                    </span>
                    <span
                      className="text-[13px] font-semibold whitespace-nowrap"
                      style={{ color: sev.color }}
                    >
                      {formatMessage(
                        { id: 'analytics.incidentMap.ordersCount' },
                        { count: row.sum },
                      )}
                    </span>
                  </div>
                  <div className="text-[11px] text-v2blueGray-600">
                    {row.code} ·{' '}
                    {sev.insufficient
                      ? formatMessage({ id: sev.labelId })
                      : formatMessage(
                          { id: 'analytics.incidentMap.maxPerZone' },
                          { count: +row.max.toFixed(1) },
                        )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      <div className="px-4 py-2 border-t border-v2blueGray-200 text-[11px] text-v2blueGray-500 italic">
        {formatMessage({ id: 'analytics.incidentMap.countries.footerHint' })}
      </div>
    </div>
  );
}

export default CountriesSidebar;
